import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { AlertCircle, Database, Download, FolderArchive, Mail, Send, Upload, RotateCcw, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const downloadBlob = (res, fallback) => {
  const match = /filename=([^;]+)/.exec(res.headers["content-disposition"] || "");
  const filename = match ? match[1].trim() : fallback;
  const url = window.URL.createObjectURL(res.data);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
  return filename;
};

const formatDate = (iso) => (iso ? new Date(iso).toLocaleString() : "Never");

const formatSize = (bytes) => {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const BackupHistoryTable = ({ rows }) => (
  <div className="space-y-2" data-testid="backup-history">
    <p className="font-medium text-slate-800">Backup history (last 10)</p>
    {rows.length === 0 ? (
      <p className="text-sm text-slate-500" data-testid="backup-history-empty">No backups have been sent yet.</p>
    ) : (
      <div className="overflow-x-auto border border-slate-200 rounded-lg">
        <table className="w-full text-sm" data-testid="backup-history-table">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="text-left px-4 py-2 font-medium">Date</th>
              <th className="text-left px-4 py-2 font-medium">Recipient</th>
              <th className="text-right px-4 py-2 font-medium">Size</th>
              <th className="text-left px-4 py-2 font-medium">Trigger</th>
              <th className="text-left px-4 py-2 font-medium">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-slate-100" data-testid={`backup-history-row-${r.id}`} title={r.error || r.filename}>
                <td className="px-4 py-2 whitespace-nowrap">{formatDate(r.sent_at)}</td>
                <td className="px-4 py-2">{r.recipient}</td>
                <td className="px-4 py-2 text-right whitespace-nowrap">{formatSize(r.size_bytes)}</td>
                <td className="px-4 py-2 capitalize">{r.trigger}</td>
                <td className="px-4 py-2">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${r.status === "sent" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                    {r.status === "sent" ? "Sent" : "Failed"}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

export const ExportBackupCard = () => {
  const [exporting, setExporting] = useState(null);

  const handleExport = async (kind) => {
    setExporting(kind);
    try {
      const path = kind === "files" ? "export-files" : "export";
      const res = await axios.get(`${API}/backup/${path}`, { responseType: "blob" });
      toast.success(`${downloadBlob(res, `trackapp_${kind}.zip`)} downloaded`);
    } catch (error) {
      toast.error(error.response?.status === 403 ? "Admin access required" : "Export failed");
    } finally {
      setExporting(null);
    }
  };

  return (
    <Card className="bg-white border-0 shadow-lg" data-testid="backup-section">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Database className="w-6 h-6 text-blue-600" />
          Data Backup
        </CardTitle>
        <p className="text-sm text-slate-600 mt-2">
          Download a read-only snapshot of every database collection as JSON files bundled in a single zip. Nothing is modified.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-2">
          <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5 shrink-0" />
          <p className="text-sm text-yellow-800">
            The backup contains all stored data verbatim, including user password hashes and integration credentials. Store the file securely.
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <Button
            onClick={() => handleExport("db")}
            disabled={exporting !== null}
            className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 shadow-lg"
            data-testid="export-database-backup-button"
          >
            <Download className="w-4 h-4 mr-2" />
            {exporting === "db" ? "Preparing backup..." : "Export Full Database Backup"}
          </Button>
          <Button onClick={() => handleExport("files")} disabled={exporting !== null} variant="outline" data-testid="export-uploaded-files-button">
            <FolderArchive className="w-4 h-4 mr-2" />
            {exporting === "files" ? "Preparing files..." : "Export Uploaded Files"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export const ScheduledBackupCard = () => {
  const [schedule, setSchedule] = useState(null);
  const [email, setEmail] = useState("");
  const [enabled, setEnabled] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);

  const load = async () => {
    const [res, hist] = await Promise.all([axios.get(`${API}/backup/schedule`), axios.get(`${API}/backup/history`)]);
    setSchedule(res.data);
    setEmail(res.data.email);
    setEnabled(res.data.enabled);
    setHistory(hist.data);
  };

  useEffect(() => {
    load().catch(() => toast.error("Could not load backup schedule"));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/backup/schedule`, { enabled, email });
      setSchedule(res.data);
      toast.success(enabled ? "Weekly backups enabled" : "Weekly backups disabled");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not save schedule");
    } finally {
      setSaving(false);
    }
  };

  const handleSendNow = async () => {
    setSending(true);
    try {
      const res = await axios.post(`${API}/backup/send-now`);
      toast.success(`Backup emailed to ${res.data.sent_to}`);
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not send backup email");
      load().catch(() => {});
    } finally {
      setSending(false);
    }
  };

  const dirty = schedule && (schedule.enabled !== enabled || schedule.email !== email);

  return (
    <Card className="bg-white border-0 shadow-lg" data-testid="scheduled-backup-section">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Mail className="w-6 h-6 text-emerald-600" />
          Scheduled Backups
        </CardTitle>
        <p className="text-sm text-slate-600 mt-2">
          Automatically email the full database backup every Sunday at 2:00 AM Eastern so a copy always exists off-platform.
        </p>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="flex items-center justify-between p-4 border border-slate-200 rounded-lg">
          <div>
            <p className="font-medium text-slate-800">Weekly backup email</p>
            <p className="text-sm text-slate-500">{schedule?.schedule || "Every Sunday at 2:00 AM Eastern (US)"}</p>
          </div>
          <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="backup-schedule-toggle" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="backup-email">Send backups to</Label>
          <Input
            id="backup-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="cgomez@cgnetworks.cloud"
            data-testid="backup-email-input"
          />
        </div>
        <div className="text-sm text-slate-600 space-y-1" data-testid="backup-last-sent">
          <p>
            <span className="font-medium">Last backup sent:</span> {formatDate(schedule?.last_sent)}
          </p>
          {schedule?.last_error && (
            <p className="text-red-600" data-testid="backup-last-error">
              <span className="font-medium">Last error:</span> {schedule.last_error}
            </p>
          )}
        </div>
        <div className="flex flex-wrap gap-3">
          <Button
            onClick={handleSave}
            disabled={saving || !dirty}
            className="bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 shadow-lg"
            data-testid="save-backup-schedule-button"
          >
            {saving ? "Saving..." : "Save Schedule"}
          </Button>
          <Button onClick={handleSendNow} disabled={sending} variant="outline" data-testid="send-backup-now-button">
            <Send className="w-4 h-4 mr-2" />
            {sending ? "Sending..." : "Send Backup Now"}
          </Button>
        </div>
        <BackupHistoryTable rows={history} />
      </CardContent>
    </Card>
  );
};

export const ImportBackupCard = () => {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(null);

  const reset = () => {
    setFile(null);
    setPreview(null);
    setConfirmText("");
    if (fileRef.current) fileRef.current.value = "";
  };

  const handleFile = (e) => {
    setFile(e.target.files?.[0] || null);
    setPreview(null);
    setConfirmText("");
  };

  const handlePreview = async () => {
    if (!file) return;
    setBusy("preview");
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await axios.post(`${API}/backup/preview`, form);
      setPreview(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not read backup file");
    } finally {
      setBusy(null);
    }
  };

  const handleRestore = async () => {
    setBusy("restore");
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("confirm", confirmText);
      const res = await axios.post(`${API}/backup/restore`, form);
      const total = Object.values(res.data.restored).reduce((a, b) => a + b, 0);
      toast.success(`Restored ${total} documents. Please sign in again with your restored credentials.`);
      reset();
      setTimeout(() => {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        window.location.href = "/";
      }, 2500);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Restore failed");
    } finally {
      setBusy(null);
    }
  };

  const needsConfirm = preview && !preview.database_fresh;
  const canRestore = preview && (!needsConfirm || confirmText === "RESTORE") && busy === null;

  return (
    <Card className="bg-white border-0 shadow-lg" data-testid="import-backup-section">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Upload className="w-6 h-6 text-orange-600" />
          Import Backup
        </CardTitle>
        <p className="text-sm text-slate-600 mt-2">
          Restore a trackapp backup zip. Preview the collection counts first; the restore only runs after you commit.
        </p>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="flex flex-wrap items-center gap-3">
          <Input ref={fileRef} type="file" accept=".zip,application/zip" onChange={handleFile} className="max-w-sm" data-testid="import-backup-file-input" />
          <Button onClick={handlePreview} disabled={!file || busy !== null} variant="outline" data-testid="preview-backup-button">
            {busy === "preview" ? "Reading..." : "Preview Backup"}
          </Button>
        </div>

        {preview && (
          <div className="space-y-4" data-testid="backup-preview">
            <div
              className={`p-4 rounded-lg border flex items-start gap-2 ${preview.database_fresh ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"}`}
              data-testid="backup-fresh-status"
            >
              {preview.database_fresh ? (
                <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5 shrink-0" />
              ) : (
                <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 shrink-0" />
              )}
              <p className={`text-sm ${preview.database_fresh ? "text-green-800" : "text-red-800"}`}>
                {preview.database_fresh
                  ? "This database is fresh (only the default admin exists). Safe to restore."
                  : "This database already contains data. Restoring will permanently REPLACE ALL existing data with the backup."}
              </p>
            </div>

            <div className="overflow-x-auto border border-slate-200 rounded-lg">
              <table className="w-full text-sm" data-testid="backup-preview-table">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium">Collection</th>
                    <th className="text-right px-4 py-2 font-medium">In backup</th>
                    <th className="text-right px-4 py-2 font-medium">Currently in database</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.collections.map((c) => (
                    <tr key={c.name} className="border-t border-slate-100" data-testid={`backup-preview-row-${c.name}`}>
                      <td className="px-4 py-2 font-mono text-slate-800">{c.name}</td>
                      <td className="px-4 py-2 text-right">{c.backup_count === null ? <span className="text-slate-400">not in backup (will be dropped)</span> : c.backup_count}</td>
                      <td className="px-4 py-2 text-right">{c.current_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {needsConfirm && (
              <div className="space-y-2">
                <Label htmlFor="restore-confirm">Type RESTORE to confirm replacing all existing data</Label>
                <Input
                  id="restore-confirm"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder="RESTORE"
                  className="max-w-xs"
                  data-testid="restore-confirm-input"
                />
              </div>
            )}

            <div className="flex flex-wrap gap-3">
              <Button
                onClick={handleRestore}
                disabled={!canRestore}
                className="bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 shadow-lg"
                data-testid="restore-backup-button"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                {busy === "restore" ? "Restoring..." : needsConfirm ? "Replace All Data & Restore" : "Restore Backup"}
              </Button>
              <Button onClick={reset} variant="ghost" disabled={busy !== null} data-testid="cancel-restore-button">
                Cancel
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export const BackupPanel = () => (
  <div className="space-y-6">
    <ExportBackupCard />
    <ScheduledBackupCard />
    <ImportBackupCard />
  </div>
);
